import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaGavel, FaPrint, FaChevronUp, FaLink } from "react-icons/fa";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const sections = [
  { id: "intro", label: "Introduction" },
  { id: "services", label: "Services & No Affiliation" },
  { id: "no-advice", label: "No Advice" },
  { id: "accounts", label: "Accounts & Security" },
  { id: "brokers", label: "Broker Responsibilities" },
  { id: "acceptable-use", label: "Acceptable Use" },
  { id: "ip", label: "Intellectual Property" },
  { id: "third-parties", label: "Third-Party Services" },
  { id: "disclaimers", label: "Disclaimers & Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "disputes", label: "Dispute Resolution" },
  { id: "changes", label: "Changes & Termination" },
  { id: "contact", label: "Contact" },
];

export default function Terms() {
  const onTocClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const anchor = (id) => (
    <a
      href={`#${id}`}
      onClick={(e) => {
        e.preventDefault();
        onTocClick(id);
      }}
      className="ml-2 inline-flex items-center text-xs text-[#002b5c]/50 hover:text-[#002b5c]"
      aria-label="Copy link to section"
      title="Copy link to section"
    >
      <FaLink />
    </a>
  );

  return (
    <div className="bg-white text-[#002b5c]">
      <Helmet>
        <title>Terms of Use | Your Choice Associates</title>
        <meta name="description" content="Terms governing use of Your Choice Associates website and services." />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="/images/hero.png" alt="" className="w-full h-[240px] md:h-[300px] object-cover" />
  <div className="absolute inset-0 bg-gradient-to-b from-[#002b5c]/90 to-[#002b5c]/75" />

        <div className="absolute inset-0 flex items-end pb-8">
          <div className="w-full px-6">
            <div className="max-w-6xl mx-auto text-white">
              <motion.div {...fade(0)} className="flex items-center gap-3 text-white/90">
                <span className="text-xs uppercase tracking-wider">Legal</span>
                <span className="text-white/40">•</span>
                <span className="text-xs">Effective: <strong>August 14, 2025</strong></span>
              </motion.div>
              <motion.h1 {...fade(0.08)} className="text-3xl md:text-5xl font-extrabold mt-2">
                Terms of Use
              </motion.h1>
              <motion.div {...fade(0.16)} className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm"
                >
                  <FaPrint /> Print
                </button>
                <span className="inline-flex items-center gap-2 text-xs bg-white/10 px-3 py-1.5 rounded-full">
                  <FaGavel /> Please read carefully
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 p-4 bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm">
              <p className="text-sm font-semibold mb-3">On this page</p>
              <nav className="space-y-2">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onTocClick(s.id)}
                    className="block w-full text-left text-sm text-[#002b5c]/80 hover:text-[#001b35]"
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <div>
            <div className="text-sm text-[#002b5c]/70 mb-6">
              <Link to="/" className="hover:underline">Home</Link> <span className="mx-1">/</span> Terms of Use
            </div>

            {[
              {
                id: "intro",
                title: "Introduction",
                body: (
                  <>
                    Welcome to{" "}
                    <a className="underline" href="https://yourchoiceassociates.org">
                      yourchoiceassociates.org
                    </a>{" "}
                    (“Site”) operated by <strong>Your Choice Associates</strong> (“YCA,” “we,” “us”).
                    By accessing or using the Site or services (“Services”), you agree to these Terms.
                    If you do not agree, do not use the Services.
                  </>
                ),
              },
              {
                id: "services",
                title: "Services & No Government Affiliation",
                body:
                  "We provide resources and connect consumers with licensed insurance brokers. We are not affiliated with or endorsed by the U.S. government or the federal Medicare program.",
              },
              {
                id: "no-advice",
                title: "No Advice",
                body:
                  "Site content is general information. Decisions about insurance should be made with a licensed professional who understands your needs. We do not guarantee plan availability, pricing, or suitability.",
              },
              {
                id: "accounts",
                title: "Accounts & Security",
                body: (
                  <ul className="list-disc pl-6 space-y-1">
                    <li>You are responsible for safeguarding login credentials and activities under your account.</li>
                    <li>Notify us immediately of suspected unauthorized use.</li>
                    <li>You must be at least 18 years old to use the Services.</li>
                  </ul>
                ),
              },
              {
                id: "brokers",
                title: "Broker Responsibilities",
                body: (
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Maintain active licenses and required E&O coverage.</li>
                    <li>Comply with applicable law, carrier rules, and CMS/Medicare marketing guidelines.</li>
                    <li>Keep profile information accurate and up to date.</li>
                  </ul>
                ),
              },
              {
                id: "acceptable-use",
                title: "Acceptable Use",
                body:
                  "Do not misuse the Services, including interfering with security/operations, scraping without permission, reverse engineering, spamming, or violating rights of others.",
              },
              {
                id: "ip",
                title: "Intellectual Property",
                body:
                  "The Site and its content, trademarks, and design are owned by us or our licensors and protected by law. You may not copy, distribute, or create derivative works without permission.",
              },
              {
                id: "third-parties",
                title: "Third-Party Services",
                body:
                  "We may link or integrate with third parties (e.g., carriers, communications providers). We are not responsible for their content or practices.",
              },
              {
                id: "disclaimers",
                title: "Disclaimers & Limitation of Liability",
                body:
                  'THE SERVICES ARE PROVIDED “AS IS” WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES.',
              },
              {
                id: "indemnification",
                title: "Indemnification",
                body:
                  "You agree to indemnify and hold YCA harmless from claims arising out of your use of the Services or violation of these Terms.",
              },
              {
                id: "disputes",
                title: "Dispute Resolution",
                body:
                  "Any dispute will be resolved through binding, individual arbitration. You waive class actions and jury trials. Governing law: New York, USA.",
              },
              {
                id: "changes",
                title: "Changes & Termination",
                body:
                  "We may modify the Services or these Terms at any time. Continued use after changes constitutes acceptance. We may suspend or terminate access for misuse or non-compliance.",
              },
              {
                id: "contact",
                title: "Contact",
                body: (
                  <>
                    Questions? Email{" "}
                    <a href="mailto:support@yourchoiceassociates.org" className="underline">
                      support@yourchoiceassociates.org
                    </a>
                    . See also our{" "}
                    <Link to="/privacy" className="underline">
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link to="/sms-terms" className="underline">
                      SMS Terms
                    </Link>
                    .
                    <p className="mt-3 text-xs text-gray-500">
                      Medicare disclaimer: We are not connected with or endorsed by the U.S. government or the federal Medicare program.
                    </p>
                  </>
                ),
              },
            ].map((sec, i) => (
              <section id={sec.id} key={sec.id} className="mb-8">
                <motion.div
                  {...fade(0.02 * i)}
                  className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8"
                >
                  <h2 className="text-2xl md:text-3xl font-extrabold">
                    {sec.title}{" "}
                    <a
                      href={`#${sec.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onTocClick(sec.id);
                      }}
                      className="ml-2 inline-flex items-center text-xs text-[#002b5c]/50 hover:text-[#002b5c]"
                      aria-label="Copy link to section"
                      title="Copy link to section"
                    >
                      <FaLink />
                    </a>
                  </h2>
                  <div className="mt-4 text-gray-700 leading-relaxed">{sec.body}</div>
                </motion.div>
              </section>
            ))}

            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-6 right-6 z-10 inline-flex items-center gap-2 rounded-full shadow-lg bg-[#002b5c] text-white px-4 py-3 hover:bg-[#001b35]"
              aria-label="Back to top"
            >
              <FaChevronUp /> Top
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
